import { DateTime } from 'luxon'

import { AudioVersion } from '@core/api/entities/audioVersion'
import { BoxAllocation } from '@core/api/entities/boxAllocation'
import { Campaign } from '@core/api/entities/campaign/campaign'
import { Group } from '@core/api/entities/group'
import { Location } from '@core/api/entities/location'
import { Playlist } from '@core/api/entities/playlist/playlist'
import { Mix } from '@core/api/entities/radio/mix'
import { Radio } from '@core/api/entities/radio/radio'
import { Schedule } from '@core/api/entities/schedule'
import { Song } from '@core/api/entities/song/song'
import { ZoneState } from '@core/api/entities/zone/state'

export class Zone {
	public id: number
	public accountId: number
	public accountName: string
	public code: string | null
	public uuid: string
	public name: string
	public storedId: number
	public storageInfo: {
		available: number | null
		required: number | null
		is_critical: boolean
	}

	public volume: number | null
	public boxVolume: number | null
	public hasVolumeChangePending: boolean
	public sendLogs: boolean
	public resetState: boolean
	public isSmartVolumeEnabled: boolean
	public smartIntegrationIds: string[] | null
	public integrationSource: string | null
	public lastSmartUpdateAt: DateTime | null
	public suspended: boolean
	public advertAudioVersionId: number | null
	public flags: string[]
	public createdAt: DateTime
	public updatedAt: DateTime

	public location?: Location
	public advertAudioVersion?: AudioVersion
	public state?: ZoneState
	public groups?: Group[]
	public boxAllocations?: BoxAllocation[]
	public lastPlayedSong?: Song | null
	public lastPlayedSchedule?: Schedule | null
	constructor(data: {
		id: number
		accountId: number
		accountName: string
		code: string | null
		uuid: string
		name: string
		storeId: number
		storageInfo: {
			available: number | null
			required: number | null
			is_critical: boolean
		}
		volume: number | null
		boxVolume: number | null
		hasVolumeChangePending: boolean
		sendLogs: boolean
		resetState: boolean
		isSmartVolumeEnabled: boolean
		smartIntegrationIds: string[] | null
		integrationSource: string | null
		lastSmartUpdateAt: DateTime | null
		suspended: boolean
		advertAudioVersionId: number | null
		flags: string[]
		createdAt: DateTime
		updatedAt: DateTime

		location?: Location | null
		state?: ZoneState | null
		groups?: Group[]
		boxAllocations?: BoxAllocation[]
		lastPlayedSong?: Song | null
		lastPlayedSchedule?: Schedule | null
		advertAudioVersion?: AudioVersion | null
	}) {
		this.id = data.id
		this.accountId = data.accountId
		this.accountName = data.accountName
		this.code = data.code
		this.uuid = data.uuid
		this.name = data.name
		this.storedId = data.storeId
		this.storageInfo = data.storageInfo
		this.volume = data.volume
		this.boxVolume = data.boxVolume
		this.hasVolumeChangePending = data.hasVolumeChangePending
		this.sendLogs = data.sendLogs
		this.resetState = data.resetState
		this.isSmartVolumeEnabled = data.isSmartVolumeEnabled
		this.smartIntegrationIds = data.smartIntegrationIds
		this.integrationSource = data.integrationSource
		this.lastSmartUpdateAt = data.lastSmartUpdateAt
		this.suspended = data.suspended
		this.advertAudioVersionId = data.advertAudioVersionId
		this.flags = data.flags
		this.createdAt = data.createdAt
		this.updatedAt = data.updatedAt

		this.location = data.location ?? undefined
		this.state = data.state ?? undefined
		this.groups = data.groups
		this.boxAllocations = data.boxAllocations
		this.lastPlayedSong = data.lastPlayedSong ?? undefined
		this.lastPlayedSchedule = data.lastPlayedSchedule ?? undefined
		this.advertAudioVersion = data.advertAudioVersion ?? undefined
	}

	static create(partial: Partial<Zone>): Zone {
		const defaults = {
			id: 0,
			accountId: 0,
			accountName: '',
			code: null,
			uuid: '',
			name: '',
			storeId: 0,
			storageInfo: {
				available: null,
				required: null,
				is_critical: false,
			},
			volume: null,
			boxVolume: null,
			hasVolumeChangePending: false,
			sendLogs: false,
			resetState: false,
			isSmartVolumeEnabled: false,
			smartIntegrationIds: null,
			integrationSource: null,
			lastSmartUpdateAt: null,
			suspended: false,
			advertAudioVersionId: null,
			flags: [],
			createdAt: DateTime.now(),
			updatedAt: DateTime.now(),

			location: undefined,
			state: undefined,
			groups: [],
			boxAllocations: [],
			lastPlayedSong: undefined,
			lastPlayedSchedule: undefined,
			advertAudioVersion: undefined,
		}

		// Back-compat / safety: some call sites spread a Zone instance (which has `storedId`)
		// into this factory. The constructor expects `storeId`.
		const storeId =
			(partial as any).storeId ?? (partial as any).storedId ?? defaults.storeId

		return new Zone({ ...defaults, ...partial, storeId })
	}

	/**
	 * Get the integration source in a more readable format
	 */
	get integrationSourcePretty(): string | null {
		if (this.integrationSource && this.integrationSource.length > 0) {
			let frags = this.integrationSource.split('_')
			for (let i = 0; i < frags.length; i++) {
				frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1)
			}
			return frags.join(' ')
		}

		return this.integrationSource
	}

	/**
	 * Check if the zone is a foot traffic integration
	 */
	get isFootTrafficIntegration(): boolean {
		return this.integrationSource?.includes('foot_traffic') ?? false
	}

	/**
	 * Check if the zone is a weather integration
	 */
	get isWeatherIntegration(): boolean {
		return this.integrationSource?.includes('weather') ?? false
	}

	/**
	 * Check if the zone has a pending code
	 */
	get hasPendingCode(): boolean {
		return !!this.code && this.code.length > 0
	}

	/**
	 * Get the active box allocation for the zone
	 */
	getActiveBoxAllocation(): BoxAllocation | null {
		if (this.boxAllocations && this.boxAllocations.length > 0) {
			const filteredBoxAllocations = this.boxAllocations.filter(
				(boxAllocation) => boxAllocation.active
			)

			if (filteredBoxAllocations.length > 0) {
				return filteredBoxAllocations[0]
			}
		}

		return null
	}

	/**
	 * Get all radios assigned to the zone
	 */
	getRadiosAssigned(): Radio[] {
		const radios: Radio[] =
			this.groups?.flatMap((group) => group.radios ?? []) ?? []
		const mixes: Mix[] =
			this.groups?.flatMap((group) => group.mixes ?? []) ?? []

		const allRadios = [
			...radios,
			...mixes.flatMap((mix) =>
				mix.mixRadios.flatMap((mixRadio) => mixRadio.radio)
			),
		]

		// Remove duplicates
		return allRadios.filter(
			(radio, index, self) =>
				radio && index === self.findIndex((t) => t && t.id === radio.id)
		) as Radio[]
	}

	/**
	 * Get all campaigns assigned to the zone. It will remove duplicates so each announcement is only returned once.
	 */
	getCampaignsAssigned(): Campaign[] {
		const campaigns =
			this.groups?.flatMap((group) => group.campaigns ?? []) ?? []

		return campaigns.filter(
			(campaign, index, self) =>
				campaign && index === self.findIndex((t) => t && t.id === campaign.id)
		)
	}

	getSchedulesAssigned(): Schedule[] {
		const schedules =
			this.groups?.flatMap((group) => group.schedules ?? []) ?? []

		// Remove duplicates
		return schedules.filter(
			(schedule, index, self) =>
				schedule && index === self.findIndex((t) => t && t.id === schedule.id)
		)
	}

	/**
	 * Get all playlists assigned to the zone through groups. It will remove duplicates.
	 */
	getPlaylistsAssigned(): Playlist[] {
		const playlists =
			this.groups?.flatMap((group) => group.playlists ?? []) ?? []

		// Remove duplicates
		return playlists.filter(
			(playlist, index, self) =>
				playlist && index === self.findIndex((t) => t && t.id === playlist.id)
		)
	}
}
